import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Avatar, IconButton, Modal, Drawer, TextField, FormControl,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ctd from 'country-telephone-data';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CheckIcon from '@mui/icons-material/Check';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import editIcon from '../../assets/edit-icon.svg';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import ChevronRight from '../../components/ChevronRight';
import { pageBoxSx, avatarSx, dispatcherSx, supervisorSx, formInputSx, allSelectCombinedSx, controlsRowSx } from './commandCentre.styles';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from '../node/createNode.styles';
import { nodeCitySx, nodeNameSx, activeTagSx, inactiveTagSx, activeDotColor, inactiveDotColor } from '../node/operatingNodes.styles';
import { getCommandCentreStaff, createCommandUser, updateCommandUser, setCommandUserActive, deactivateCommandCentre } from '../../services/commandCentreService';
import CreateCommandCentreDrawer from './CreateCommandCentreDrawer';

interface Props {
  commandCentre: any;
  onBack: () => void;
  onUpdated?: (msg?: string) => void;
}

type StaffRow = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'Supervisor' | 'Dispatcher';
  status: 'Active' | 'Inactive';
};

const CommandCentreDetail: React.FC<Props> = ({ commandCentre: initialCC, onBack, onUpdated }) => {
  const [cc, setCC] = useState<any>(initialCC);
  const [activeTab, setActiveTab] = useState<'overview' | 'staff'>('overview');

  // Edit CC drawer
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  // Staff overview stats (loaded on mount for the Overview tab cards)
  const [staffStats, setStaffStats] = useState({ total: 0, supervisors: 0, dispatchers: 0 });

  const loadStaffStats = React.useCallback(async (signal?: AbortSignal) => {
    if (!cc?.id) return;
    try {
      const resp = await getCommandCentreStaff(cc.id, 1, 200, signal);
      const users = resp.command_users ?? [];
      const activeUsers = users.filter((u: any) => u.active !== false);
      const total = activeUsers.length;
      const supervisors = activeUsers.filter((u: any) => (u.user_role ?? '').toUpperCase() === 'SUPERVISOR').length;
      const dispatchers = activeUsers.filter((u: any) => (u.user_role ?? '').toUpperCase() === 'DISPATCHER').length;
      setStaffStats({ total, supervisors, dispatchers });
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
    }
  }, [cc?.id]);

  useEffect(() => {
    const controller = new AbortController();
    loadStaffStats(controller.signal);
    return () => controller.abort();
  }, [loadStaffStats]);

  // Staff state
  const [staffRows, setStaffRows] = useState<StaffRow[]>([]);
  const [staffPagination, setStaffPagination] = useState({ page: 0, pageSize: 10 });
  const [staffRowCount, setStaffRowCount] = useState(0);
  const [staffLoading, setStaffLoading] = useState(false);

  // Staff filters
  const [staffRoleFilter, setStaffRoleFilter] = useState<string | null>(null);
  const [staffStatusFilter, setStaffStatusFilter] = useState<string | null>(null);

  // Add / Edit Staff drawer
  const [staffDrawerOpen, setStaffDrawerOpen] = useState(false);
  const [staffDrawerInitial, setStaffDrawerInitial] = useState<any>(null);

  // Confirm deactivate modal (for staff)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<StaffRow | null>(null);

  // Confirm deactivate modal (for the command centre itself)
  const [confirmCCOpen, setConfirmCCOpen] = useState(false);

  const loadStaff = React.useCallback(async (signal?: AbortSignal) => {
    if (!cc?.id) return;
    setStaffLoading(true);
    try {
      const apiPage = (staffPagination.page ?? 0) + 1;
      const resp = await getCommandCentreStaff(cc.id, apiPage, staffPagination.pageSize, signal);
      const users = resp.command_users;
      const normalized: StaffRow[] = users.map((u: any, idx: number) => ({
        id: u.user_id ?? u.id ?? idx,
        firstName: (u.name ?? '').split(' ')[0] || '',
        lastName: (u.name ?? '').split(' ').slice(1).join(' ') || '',
        email: u.email ?? '',
        phone: u.phone ?? '',
        role: (u.user_role === 'SUPERVISOR' || u.user_role === 'Supervisor') ? 'Supervisor' : 'Dispatcher',
        status: u.active === false ? 'Inactive' : 'Active',
      }));
      let filtered = normalized;
      if (staffRoleFilter) filtered = filtered.filter((r) => r.role === staffRoleFilter);
      if (staffStatusFilter) filtered = filtered.filter((r) => r.status === staffStatusFilter);
      setStaffRows(filtered);
      const total = resp.meta?.total ?? resp.meta?.count ?? normalized.length;
      setStaffRowCount(Number(total) || filtered.length);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
    } finally {
      setStaffLoading(false);
    }
  }, [cc?.id, staffPagination, staffRoleFilter, staffStatusFilter]);

  useEffect(() => {
    if (activeTab === 'staff') {
      const controller = new AbortController();
      loadStaff(controller.signal);
      return () => controller.abort();
    }
  }, [activeTab, loadStaff]);

  const staffColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={avatarSx}>{params.row.firstName[0] ?? ''}{params.row.lastName[0] ?? ''}</Avatar>
          <Typography sx={nodeNameSx}>{params.row.firstName} {params.row.lastName}</Typography>
        </Box>
      ),
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 160, sortable: false, renderCell: (p: GridRenderCellParams<any>) => <Typography sx={nodeCitySx as any}>{p.row.email}</Typography> },
    {
      field: 'role', headerName: 'Role', flex: 0.6, minWidth: 120, sortable: false,
      renderCell: (p: GridRenderCellParams<any>) =>
        p.value === 'Supervisor'
          ? <Box component="span" sx={{ ...supervisorSx, px: 2 }}>{p.value}</Box>
          : <Box component="span" sx={{ ...dispatcherSx, px: 2 }}>{p.value}</Box>,
    },
    {
      field: 'status', headerName: 'Status', flex: 0.6, minWidth: 120, sortable: false,
      renderCell: (p: GridRenderCellParams<any>) =>
        p.value === 'Active'
          ? <Box component="span" sx={activeTagSx}><Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: activeDotColor, top: '9.25px', left: '10px' }} /><Box component="span" sx={{ pl: '18px' }}>{p.value}</Box></Box>
          : <Box component="span" sx={inactiveTagSx}><Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px' }} /><Box component="span" sx={{ pl: '18px' }}>{p.value}</Box></Box>,
    },
    {
      field: 'actions', headerName: 'Actions', flex: 0.5, minWidth: 120, sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const isInactive = params.row.status === 'Inactive';
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" disabled={isInactive} onClick={() => { if (!isInactive) { setStaffDrawerInitial(params.row); setStaffDrawerOpen(true); } }}>
              <Box component="img" src={editIcon} alt="Edit" sx={{ width: 14, height: 14, opacity: isInactive ? 0.4 : 1 }} />
            </IconButton>
            {/* KAN-129: power toggle stays enabled for inactive rows so staff can be reactivated. */}
            <IconButton size="small" title={isInactive ? 'Activate' : 'Deactivate'} onClick={() => { setConfirmTarget(params.row); setConfirmOpen(true); }}>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <PowerSettingsNewIcon sx={{ color: isInactive ? '#9CA3AF' : '#6B7280', fontSize: '16px' }} />
                {isInactive && <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: '#9CA3AF', transform: 'rotate(45deg)' }} />}
              </Box>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const confirmToggleStaff = async () => {
    if (!confirmTarget) return;
    // KAN-129: derive the action from the row's current status so the same
    // confirm flow handles both activate and deactivate.
    const nextActive = confirmTarget.status === 'Inactive';
    try {
      await setCommandUserActive(confirmTarget.id, nextActive);
      setStaffRows((r) => r.map((x) => x.id === confirmTarget.id ? { ...x, status: nextActive ? 'Active' : 'Inactive' } : x));
      loadStaffStats();
      window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: nextActive ? 'Staff account activated' : 'Staff account deactivated' } }));
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate' : 'Failed to deactivate') } }));
    }
    setConfirmOpen(false);
    setConfirmTarget(null);
  };

  const ccStatus = cc?.active === false ? 'Inactive' : 'Active';
  const locationParts = [cc?.city, cc?.state].filter(Boolean).join(', ');
  const capacityPercent = staffStats.total > 0 ? Math.min(Math.round(staffStats.total * 10), 100) : 0;

  const tabStyle = (tab: 'overview' | 'staff') => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: activeTab === tab ? 600 : 400,
    fontSize: '13px',
    lineHeight: '19.5px',
    color: activeTab === tab ? '#49B6D3' : '#6B7280',
    borderBottom: activeTab === tab ? '2px solid #49B6D3' : '2px solid transparent',
    cursor: 'pointer',
    pb: '8px',
    textTransform: 'none' as const,
    background: 'none',
    border: 'none',
    padding: '0 0 8px 0',
    mr: 3,
  });

  const sectionLabelSx = {
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 600,
    fontSize: '11px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#9CA3AF',
    mb: 1,
  };

  return (
    <Box sx={pageBoxSx}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button size="small" onClick={onBack} sx={{ textTransform: 'none', p: 0, minWidth: 0, color: '#6B7280', fontSize: '13px' }}>
          Central Command
        </Button>
        <Typography sx={{ color: '#6B7280' }}><ChevronRight /></Typography>
        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{cc?.name}</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px', letterSpacing: '-0.45px', mb: 0.5 }}>
            {cc?.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ccStatus === 'Active' ? '#22C55E' : '#9CA3AF', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', color: '#6B7280' }}>
              {ccStatus}
              {locationParts ? ` · ${locationParts}` : ''}
              {cc?.radius != null ? ` · ${parseFloat((cc.radius / 1000).toFixed(2))} km coverage` : ''}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={() => setEditDrawerOpen(true)}
          sx={{ height: '36px', borderRadius: '10px', border: '1px solid #E5E7EB', backgroundColor: 'transparent', color: '#111827', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2.5, whiteSpace: 'nowrap', flexShrink: 0, ml: 2 }}
        >
          Edit center
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.06)', mb: 3 }}>
        <Box component="button" onClick={() => setActiveTab('overview')} sx={tabStyle('overview')}>Overview</Box>
        <Box component="button" onClick={() => setActiveTab('staff')} sx={tabStyle('staff')}>Staff</Box>
      </Box>

      {/* ── Overview Tab ── */}
      {activeTab === 'overview' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: '860px' }}>

          {/* Stat Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Staff', value: staffStats.total, accent: '#49B6D3' },
              { label: 'Supervisors', value: staffStats.supervisors, accent: '#8B5CF6' },
              { label: 'Dispatchers', value: staffStats.dispatchers, accent: '#60AF97' },
            ].map(({ label, value, accent }) => (
              <Box key={label} sx={{ flex: 1, minWidth: 0, height: 76, borderRadius: '12px', border: '1px solid #E5E7EB', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', pl: '20px' }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent }} />
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '30px', lineHeight: 1, color: '#111827' }}>{value}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', color: '#6B7280' }}>{label}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* LOCATION */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Location</Typography>
            <Box sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', p: '16px 20px', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#49B6D3' }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '14px', color: '#111827' }}>{locationParts || '—'}</Typography>
                {cc?.address && (
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: '#6B7280', mt: 0.25 }}>{cc.address}</Typography>
                )}
                {cc?.latitude != null && cc?.longitude != null && (
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '12px', color: '#9CA3AF', mt: 0.25 }}>
                    {Number(cc.latitude).toFixed(5)}, {Number(cc.longitude).toFixed(5)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* COVERAGE */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Coverage</Typography>
            <Box sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', p: '16px 20px', display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* Circular badge */}
              <Box sx={{ width: 76, height: 76, borderRadius: '50%', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#49B6D3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#143133', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '11px', color: '#fff', lineHeight: 1 }}>
                      {cc?.radius != null ? parseFloat((cc.radius / 1000).toFixed(2)) : '—'}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '9px', color: '#fff', lineHeight: 1, mt: 0.25 }}>km</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                  {cc?.radius != null ? `${parseFloat((cc.radius / 1000).toFixed(2))} km radius` : 'No coverage set'}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: '#6B7280', mt: 0.5 }}>
                  Geofence coverage area for this command centre
                </Typography>
                <Box sx={{ mt: 1.5, height: 6, borderRadius: 3, bgcolor: '#F3F4F6', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${capacityPercent}%`, bgcolor: '#49B6D3', borderRadius: 3 }} />
                </Box>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                  {capacityPercent}% active capacity
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* STATUS */}
          <Box>
            <Typography sx={sectionLabelSx}>Status</Typography>
            <Box sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', p: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ccStatus === 'Active' ? '#22C55E' : '#9CA3AF', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '14px', color: '#111827' }}>{ccStatus}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: '#6B7280', mt: 0.25 }}>
                    {ccStatus === 'Active'
                      ? 'This command centre is currently operational and monitoring.'
                      : 'This command centre is currently inactive and not monitoring.'}
                  </Typography>
                </Box>
              </Box>
              {ccStatus === 'Active' && (
                <Button
                  onClick={() => setConfirmCCOpen(true)}
                  sx={{ height: '36px', borderRadius: '10px', border: '1px solid #FECACA', bgcolor: '#FEF2F2', color: '#EF4444', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2, flexShrink: 0, '&:hover': { bgcolor: '#FEE2E2' } }}
                >
                  Deactivate
                </Button>
              )}
            </Box>
          </Box>

        </Box>
      )}

      {/* ── Staff Tab ── */}
      {activeTab === 'staff' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                sx={{ ...allSelectCombinedSx, minWidth: 160 }}
                options={['Supervisor', 'Dispatcher']}
                value={staffRoleFilter}
                onChange={(_e, val) => { setStaffRoleFilter(val); setStaffPagination((p) => ({ ...p, page: 0 })); }}
                renderInput={(params) => <TextField {...params} placeholder="All Roles" />}
                clearOnEscape
              />
              <Autocomplete
                size="small"
                sx={{ ...allSelectCombinedSx, minWidth: 160 }}
                options={['Active', 'Inactive']}
                value={staffStatusFilter}
                onChange={(_e, val) => { setStaffStatusFilter(val); setStaffPagination((p) => ({ ...p, page: 0 })); }}
                renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
                clearOnEscape
              />
            </Box>
            <Button
              onClick={() => { setStaffDrawerInitial(null); setStaffDrawerOpen(true); }}
              sx={{ height: '36px', borderRadius: '10px', backgroundColor: '#39A9B1', color: '#FFFFFF', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2, flexShrink: 0, '&:hover': { backgroundColor: '#2d8f96' } }}
            >
              + Add Staff
            </Button>
          </Box>
          <Paper sx={{ borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', width: '100%', maxWidth: '1100px', alignSelf: 'flex-start', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            <DataGrid
              loading={staffLoading}
              rows={staffRows}
              columns={staffColumns}
              columnHeaderHeight={40}
              rowHeight={48}
              pagination
              paginationMode="server"
              paginationModel={staffPagination}
              onPaginationModelChange={setStaffPagination}
              rowCount={staffRowCount}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB !important', borderBottom: '1px solid rgba(0,0,0,0.06)', minHeight: '40px !important', maxHeight: '40px !important' },
                '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFB !important' },
                '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '11px', lineHeight: '16.5px', letterSpacing: '0.06px' },
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .MuiDataGrid-cell': { borderRight: 'none', display: 'flex', alignItems: 'center' },
                '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(0,0,0,0.06)' },
              }}
            />
          </Paper>
        </Box>
      )}

      {/* Edit CC Drawer */}
      <CreateCommandCentreDrawer
        open={editDrawerOpen}
        initial={cc}
        onClose={() => setEditDrawerOpen(false)}
        onSaved={(msg, updatedData) => {
          setEditDrawerOpen(false);
          if (updatedData) setCC((prev: any) => ({ ...prev, ...updatedData }));
          if (msg) window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: msg } }));
          if (onUpdated) onUpdated(msg);
        }}
      />

      {/* Add / Edit Staff Drawer */}
      <AddStaffDrawer
        open={staffDrawerOpen}
        initial={staffDrawerInitial}
        commandCentreId={cc?.id}
        onClose={() => setStaffDrawerOpen(false)}
        onSaved={() => {
          setStaffDrawerOpen(false);
          loadStaff();
          loadStaffStats();
        }}
      />

      {/* Confirm Deactivate Command Centre Modal */}
      <Modal open={confirmCCOpen} onClose={() => setConfirmCCOpen(false)}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography sx={modalTitleSx}>Deactivate Command Centre?</Typography>
            <IconButton size="small" onClick={() => setConfirmCCOpen(false)} sx={modalCloseIconSx}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>
          <Box sx={modalContentSx}>
            <Box sx={modalWarningRowSx}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2' }}>
                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', objectFit: 'contain' }} />
              </Box>
              <Typography sx={modalBodyTextSx}>
                Deactivating "{cc?.name}" will disable this command centre. This action cannot be undone.
              </Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={modalCancelButtonSx} onClick={() => setConfirmCCOpen(false)}>Cancel</Button>
              <Button sx={modalDeactivateButtonSx} onClick={async () => {
                try {
                  await deactivateCommandCentre(cc.id);
                  setCC((prev: any) => ({ ...prev, active: false }));
                  window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Command centre deactivated' } }));
                  if (onUpdated) onUpdated('Command centre deactivated');
                } catch (err: any) {
                  window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to deactivate' } }));
                }
                setConfirmCCOpen(false);
              }}>Deactivate</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Confirm Activate/Deactivate Staff Modal — KAN-129 */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography sx={modalTitleSx}>{confirmTarget?.status === 'Inactive' ? 'Activate account?' : 'Deactivate account?'}</Typography>
            <IconButton size="small" onClick={() => setConfirmOpen(false)} sx={modalCloseIconSx}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>
          <Box sx={modalContentSx}>
            <Box sx={modalWarningRowSx}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2' }}>
                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', objectFit: 'contain' }} />
              </Box>
              <Typography sx={modalBodyTextSx}>
                {confirmTarget
                  ? (confirmTarget.status === 'Inactive'
                    ? `"${confirmTarget.firstName} ${confirmTarget.lastName}" will regain access to the Command Dashboard.`
                    : `"${confirmTarget.firstName} ${confirmTarget.lastName}" will lose access to the Command Dashboard immediately.`)
                  : 'This account access will change immediately.'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={modalCancelButtonSx} onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button sx={modalDeactivateButtonSx} onClick={confirmToggleStaff}>{confirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

// ─── Add / Edit Staff Drawer ─────────────────────────────────────────────────

interface AddStaffProps {
  open: boolean;
  initial?: any;
  commandCentreId?: number;
  onClose: () => void;
  onSaved: () => void;
}

const AddStaffDrawer: React.FC<AddStaffProps> = ({ open, initial, commandCentreId, onClose, onSaved }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'Dispatcher' | 'Supervisor'>('Dispatcher');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFullName(initial ? `${initial.firstName ?? ''} ${initial.lastName ?? ''}`.trim() : '');
      setEmail(initial?.email ?? '');
      const initialPhone = initial?.phone ?? '';
      const match = String(initialPhone).match(/^(\+\d{1,4})\s?/);
      setCountryCode(match ? match[1] : '+91');
      setPhone(initialPhone.replace(/^(\+\d{1,4})\s?/, ''));
      setRole(initial?.role ?? 'Dispatcher');
      setFormError(null);
    }
  }, [open, initial]);

  const isValid = () => Boolean(fullName.trim()) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSave = async () => {
    if (!isValid()) { setFormError('Name and valid email are required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const roleVal = role === 'Supervisor' ? 'SUPERVISOR' : 'DISPATCHER';
      const phoneVal = phone.trim() ? `${countryCode} ${phone.trim()}` : '';
      if (initial && (initial.user_id || initial.id)) {
        const patch: any = {};
        const nameVal = fullName.trim();
        const origName = `${initial.firstName ?? ''} ${initial.lastName ?? ''}`.trim();
        if (nameVal !== origName) patch.name = nameVal;
        if (email.trim() !== (initial.email ?? '')) patch.email = email.trim();
        if (phoneVal !== (initial.phone ?? '')) patch.phone = phoneVal;
        if (roleVal !== ((initial.user_role ?? initial.role ?? '').toString().toUpperCase())) patch.role = roleVal;
        if (Object.keys(patch).length === 0) {
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'No changes to save' } }));
        } else {
          await updateCommandUser(initial.user_id ?? initial.id, patch);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Staff updated successfully' } }));
        }
      } else {
        const payload: any = { name: fullName.trim(), email: email.trim(), role: roleVal };
        if (phoneVal) payload.phone = phoneVal;
        if (commandCentreId) payload.command_centre_id = commandCentreId;
        await createCommandUser(payload);
        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Staff added successfully' } }));
      }
      onSaved();
    } catch (err: any) {
      setFormError(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const labelSx = { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '20px', color: '#374151', mb: 0.5 };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 480, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '24px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.31px' }}>
          {initial ? 'Edit Staff' : 'Add Staff'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ width: 16, height: 16, minWidth: 16, padding: 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '24px', py: '20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {formError && (
          <Box sx={{ borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', color: '#374151', flex: 1 }}>{formError}</Typography>
            <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, ml: 1 }}>×</Box>
          </Box>
        )}

        <Box>
          <Typography sx={labelSx}>Full Name *</Typography>
          <TextField fullWidth size="small" placeholder="e.g. Sarah Chen" value={fullName} onChange={(e) => setFullName(e.target.value)} sx={formInputSx} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Email Address *</Typography>
          <TextField fullWidth size="small" placeholder="name@cmd.wualt.co" value={email} onChange={(e) => setEmail(e.target.value)} sx={formInputSx} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Phone (optional)</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <FormControl size="small" sx={{ width: '160px', flexShrink: 0 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ height: '41.5px', borderRadius: '10px', paddingLeft: '12px', border: '1px solid #E5E7EB', background: 'white', fontSize: '13px', fontFamily: 'Inter, Arial, sans-serif' }}
              >
                {(ctd as any).allCountries.map((c: any) => {
                  const rawName = c.name ?? c[0] ?? String(c);
                  const cleanName = String(rawName).replace(/\s*\(.*\)\s*$/, '');
                  return (
                    <option key={c.iso2 ?? cleanName} value={c.dialCode ? `+${c.dialCode}` : ''}>{`${cleanName} (+${c.dialCode})`}</option>
                  );
                })}
              </select>
            </FormControl>
            <TextField
              fullWidth size="small" placeholder="7700 900000"
              value={phone} onChange={(e) => setPhone(e.target.value)}
              sx={formInputSx}
            />
          </Box>
        </Box>

        {/* Role selection */}
        <Box>
          <Typography sx={{ ...labelSx, mb: 1 }}>Role</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {(['Dispatcher', 'Supervisor'] as const).map((r) => (
              <Button
                key={r}
                onClick={() => setRole(r)}
                sx={{
                  flex: 1,
                  height: 72,
                  justifyContent: 'space-between',
                  borderRadius: '14px',
                  border: `1px solid ${role === r ? '#39A9B1' : '#E5E7EB'}`,
                  padding: '13px',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{r}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                    {r === 'Dispatcher' ? 'Manages real-time incident response.' : 'Oversees dispatchers and operations.'}
                  </Typography>
                </Box>
                {role === r && (
                  <Box sx={{ width: 16, height: 16, borderRadius: '9999px', backgroundColor: '#39A9B1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckIcon sx={{ width: 9, height: 9, color: '#FFFFFF' }} />
                  </Box>
                )}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: '24px', py: '16px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
        <Button onClick={onClose} sx={{ height: '36px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'transparent', color: '#374151', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isValid() || saving}
          sx={{ height: '36px', borderRadius: '10px', backgroundColor: '#39A9B1', color: '#FFFFFF', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2, '&:hover': { backgroundColor: '#2d8f96' }, '&.Mui-disabled': { backgroundColor: '#39A9B1', opacity: 0.5, color: '#FFFFFF' } }}
        >
          {saving ? 'Saving…' : (initial ? 'Update' : 'Add Staff')}
        </Button>
      </Box>
    </Drawer>
  );
};

export default CommandCentreDetail;
